import { useState, useEffect, useRef } from "react";
import { X, Search as SearchIcon } from "lucide-react";
import { Link } from "react-router";
import { motion, AnimatePresence } from "motion/react";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import { useAdminData } from "./admin/AdminDataContext";
import { useLanguage } from "./LanguageContext";
import { localized } from "./admin/TranslationsPanel";
import { useTheme } from "./ThemeInjector";

interface SearchOverlayProps {
  isOpen: boolean;
  onClose: () => void;
}

export function SearchOverlay({ isOpen, onClose }: SearchOverlayProps) {
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const { products, journal } = useAdminData();
  const { t, lang } = useLanguage();
  const theme = useTheme();

  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose]);

  const q = query.toLowerCase().trim();

  const filteredProducts = q
    ? products.filter(
        (p) =>
          localized(p, "name", lang).toLowerCase().includes(q) ||
          p.category.toLowerCase().includes(q) ||
          p.description.toLowerCase().includes(q)
      )
    : [];

  const filteredJournal = q
    ? journal
        .filter((j) => j.published)
        .filter(
          (j) =>
            localized(j, "title", lang).toLowerCase().includes(q) ||
            localized(j, "excerpt", lang).toLowerCase().includes(q) ||
            j.author.toLowerCase().includes(q)
        )
    : [];

  const hasResults = filteredProducts.length > 0 || filteredJournal.length > 0;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[60] backdrop-blur-lg flex flex-col"
          style={{ backgroundColor: `${theme.bg}fa` }}
        >
          {/* Header */}
          <div className="max-w-3xl w-full mx-auto px-6 pt-8 pb-4">
            <div className="flex items-center justify-between mb-8">
              <h2
                className="tracking-[0.1em]"
                style={{ fontFamily: theme.fontHeading, fontSize: "24px", fontWeight: 400, color: theme.fg }}
              >
                {t("search.title")}
              </h2>
              <button
                onClick={onClose}
                className="p-2 transition-colors focus:outline-none focus:ring-2 rounded-lg"
                style={{ color: theme.muted }}
                aria-label="Close search"
              >
                <X size={22} strokeWidth={1.5} />
              </button>
            </div>

            {/* Search Input */}
            <div className="relative">
              <SearchIcon
                size={18}
                strokeWidth={1.5}
                className="absolute left-4 top-1/2 -translate-y-1/2"
                style={{ color: theme.border }}
              />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("search.placeholder")}
                className="w-full pl-12 pr-4 py-4 border rounded-xl focus:outline-none transition-colors"
                style={{ fontFamily: theme.fontBody, fontSize: "15px", fontWeight: 300, backgroundColor: theme.card, borderColor: theme.border, color: theme.fg }}
                aria-label={t("search.placeholder")}
              />
            </div>
          </div>

          {/* Results */}
          <div className="flex-1 overflow-y-auto">
            <div className="max-w-3xl w-full mx-auto px-6 pb-12">
              {q && !hasResults && (
                <div className="text-center py-16">
                  <p
                    className="mb-2"
                    style={{ fontFamily: theme.fontBody, fontSize: "15px", fontWeight: 300, color: theme.muted }}
                  >
                    {t("search.no_results")}
                  </p>
                  <p
                    style={{ fontFamily: theme.fontBody, fontSize: "13px", fontWeight: 300, color: theme.border }}
                  >
                    {t("search.try_different")}
                  </p>
                </div>
              )}

              {/* Products */}
              {filteredProducts.length > 0 && (
                <div className="mb-10">
                  <p
                    className="tracking-[0.3em] uppercase mb-5"
                    style={{ fontFamily: theme.fontBody, fontSize: "11px", color: theme.accent }}
                  >
                    {t("search.products")}
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {filteredProducts.slice(0, 6).map((product) => (
                      <Link
                        key={product.id}
                        to={`/product/${product.slug}`}
                        onClick={onClose}
                        className="group"
                      >
                        <div className="overflow-hidden rounded-lg mb-3" style={{ backgroundColor: `${theme.border}40` }}>
                          <ImageWithFallback
                            src={product.images[0]}
                            alt={product.name}
                            className="w-full aspect-square object-cover group-hover:scale-[1.03] transition-transform duration-500"
                          />
                        </div>
                        <h3
                          className="tracking-[0.04em] transition-colors"
                          style={{ fontFamily: theme.fontHeading, fontSize: "16px", fontWeight: 500, color: theme.fg }}
                        >
                          {localized(product, "name", lang)}
                        </h3>
                        <p
                          style={{ fontFamily: theme.fontBody, fontSize: "13px", fontWeight: 300, color: theme.muted }}
                        >
                          &euro;{product.price}
                        </p>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Journal */}
              {filteredJournal.length > 0 && (
                <div>
                  <p
                    className="tracking-[0.3em] uppercase mb-5"
                    style={{ fontFamily: theme.fontBody, fontSize: "11px", color: theme.accent }}
                  >
                    {t("search.journal_entries")}
                  </p>
                  <div className="space-y-4">
                    {filteredJournal.slice(0, 4).map((entry) => (
                      <Link
                        key={entry.id}
                        to={`/journal/${entry.slug}`}
                        onClick={onClose}
                        className="flex gap-4 items-center group p-3 rounded-lg transition-colors"
                        style={{ backgroundColor: "transparent" }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = `${theme.border}40`)}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                      >
                        {entry.image && (
                          <div className="w-16 h-16 rounded-lg overflow-hidden shrink-0" style={{ backgroundColor: `${theme.border}40` }}>
                            <ImageWithFallback
                              src={entry.image}
                              alt={entry.title}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                        <div className="min-w-0">
                          <h4
                            className="transition-colors truncate"
                            style={{ fontFamily: theme.fontHeading, fontSize: "17px", fontWeight: 500, color: theme.fg }}
                          >
                            {localized(entry, "title", lang)}
                          </h4>
                          <p
                            className="truncate"
                            style={{ fontFamily: theme.fontBody, fontSize: "12px", fontWeight: 300, color: theme.muted }}
                          >
                            {localized(entry, "excerpt", lang)}
                          </p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}